// Global Instructions Rule Applied!
//
// The stored notices of a capital call (capital call notices, stage 3):
// the list behind the form's Notices section and the generate action. The
// stored PDFs themselves download through the generic files service. And
// (stage 7) the notice email: what "Send notices" would create, the create
// itself and the refresh of a draft.

import { API_ROUTES, buildFullUrl, getBackendBaseUrl } from '../../../core/utils/endpoints.ts'
import {
  CapitalCallNotices,
  validateCapitalCallNotices
} from '../../../state_manager/data_models/capital_call_notices.ts'
import {
  CapitalCallNoticeCampaignPlan,
  validateCapitalCallNoticeCampaignPlan
} from '../../../state_manager/data_models/capital_call_notice_campaign_plan.ts'

const BACKEND_BASE_URL = getBackendBaseUrl()

function authHeaders(accessToken: string | null): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`
  return headers
}

/**
 * Per LP the stored notice the list shows (status, file, out of date,
 * stalled), the problems that stop the notices being produced, and the
 * counts. Works for a Draft call, which lists its LPs with nothing stored.
 */
export const fetchCapitalCallNotices = async (
  capitalCallId: string,
  accessToken: string | null = null
): Promise<{ data: CapitalCallNotices | null; error?: string }> => {
  try {
    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CAPITAL_CALLS.NOTICES, { id: capitalCallId }, {})
    const response = await fetch(url, {
      method: API_ROUTES.CAPITAL_CALLS.NOTICES.method,
      headers: authHeaders(accessToken)
    })

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      const body = (await response.json().catch(() => ({}))) as { message?: string }
      throw new Error(body.message || `Failed to fetch the notices: ${response.status} ${response.statusText}`)
    }

    const json = (await response.json()) as { data?: unknown }
    if (!json.data) return { data: null }
    return { data: validateCapitalCallNotices(json.data) }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching capital call notices:', error)
    return { data: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

/**
 * Generate (or regenerate) the stored notices of an issued call: every LP's,
 * or only the given commitments'. The server answers as soon as the notices
 * are queued; the list then shows them through to Generated or Failed.
 * A refusal (terms incomplete, call not issued) comes back as `error`, the
 * server's own sentence.
 */
export const generateCapitalCallNotices = async (
  capitalCallId: string,
  fundCommitmentIds: string[] | null = null,
  accessToken: string | null = null
): Promise<{ count: number | null; error?: string }> => {
  try {
    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CAPITAL_CALLS.NOTICES_GENERATE, { id: capitalCallId }, {})
    const response = await fetch(url, {
      method: API_ROUTES.CAPITAL_CALLS.NOTICES_GENERATE.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify({ data: fundCommitmentIds ? { fund_commitment_ids: fundCommitmentIds } : {} })
    })
    const json = (await response.json().catch(() => ({}))) as {
      data?: { count?: number } | null
      error?: string
      message?: string
    }
    if (!response.ok) {
      if (response.status === 401) return { count: null, error: 'Authentication failed - please log in again' }
      return { count: null, error: json.error || json.message || `Request failed: ${response.status}` }
    }
    return { count: json.data?.count ?? 0 }
  } catch (error) {
    return { count: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

/** The server's own sentence for a failed request, whichever key carries it. */
async function failureText(response: Response): Promise<string> {
  if (response.status === 401) return 'Authentication failed - please log in again'
  const body = (await response.json().catch(() => ({}))) as { error?: string; message?: string }
  return body.error || body.message || `Request failed: ${response.status}`
}

/**
 * What "Send notices" would create for the call: per LP account its notices,
 * its recipients and the merge fields of its email, and what blocks the
 * send. `onlyUnsent` leaves out the notices already sent.
 */
export const fetchNoticeCampaignPlan = async (
  capitalCallId: string,
  onlyUnsent = false,
  accessToken: string | null = null
): Promise<{ data: CapitalCallNoticeCampaignPlan | null; error?: string }> => {
  try {
    const url = buildFullUrl(
      BACKEND_BASE_URL,
      API_ROUTES.CAPITAL_CALLS.NOTICE_CAMPAIGN_PLAN,
      { id: capitalCallId },
      onlyUnsent ? { only_unsent: true } : {}
    )
    const response = await fetch(url, {
      method: API_ROUTES.CAPITAL_CALLS.NOTICE_CAMPAIGN_PLAN.method,
      headers: authHeaders(accessToken)
    })
    if (!response.ok) return { data: null, error: await failureText(response) }
    const json = (await response.json()) as { data?: unknown }
    if (!json.data) return { data: null, error: 'The server returned no plan' }
    return { data: validateCapitalCallNoticeCampaignPlan(json.data) }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching the notice email plan:', error)
    return { data: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

export interface CreatedNoticeCampaign {
  email_campaigns__id: string
  email_campaigns__name: string
  /** The draft exists but could not be filled: the queue refuses it until
   *  "Refresh from capital call" has run. */
  prog__sync_error?: string | null
}

/**
 * Create the draft notice email of an issued call. A refusal (the call is
 * not issued, notices missing or out of date, nobody to send to) comes back
 * as `error`, the server's own sentence.
 */
export const createNoticeCampaign = async (
  capitalCallId: string,
  onlyUnsent = false,
  accessToken: string | null = null
): Promise<{ data: CreatedNoticeCampaign | null; error?: string }> => {
  try {
    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CAPITAL_CALLS.NOTICE_CAMPAIGN_CREATE, { id: capitalCallId }, {})
    const response = await fetch(url, {
      method: API_ROUTES.CAPITAL_CALLS.NOTICE_CAMPAIGN_CREATE.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify({ data: { only_unsent: onlyUnsent } })
    })
    if (!response.ok) return { data: null, error: await failureText(response) }
    const json = (await response.json()) as { data?: Partial<CreatedNoticeCampaign> | null }
    const id = json.data?.email_campaigns__id
    if (!id) return { data: null, error: 'The server did not return the email it created' }
    return {
      data: {
        email_campaigns__id: id,
        email_campaigns__name: json.data?.email_campaigns__name || 'Capital call notices',
        prog__sync_error: json.data?.prog__sync_error ?? null
      }
    }
  } catch (error) {
    return { data: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

/**
 * "Refresh from capital call" on a draft notice email: its recipients and
 * per-account notices are rebuilt from the call as it is now.
 */
export const refreshNoticeCampaign = async (
  capitalCallId: string,
  emailCampaignId: string,
  accessToken: string | null = null
): Promise<{ success: boolean; recipientCount?: number; error?: string }> => {
  try {
    const url = buildFullUrl(
      BACKEND_BASE_URL,
      API_ROUTES.CAPITAL_CALLS.NOTICE_CAMPAIGN_REFRESH,
      { id: capitalCallId, campaignId: emailCampaignId },
      {}
    )
    const response = await fetch(url, {
      method: API_ROUTES.CAPITAL_CALLS.NOTICE_CAMPAIGN_REFRESH.method,
      headers: authHeaders(accessToken)
    })
    if (!response.ok) return { success: false, error: await failureText(response) }
    const json = (await response.json().catch(() => ({}))) as { data?: { recipient_count?: number } | null }
    return { success: true, recipientCount: json.data?.recipient_count ?? 0 }
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}
